import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import useFetch from '../../api/useFetch';
import { useTranslation } from 'react-i18next';
import { tokens } from '../../global/theme/tokens';
import TypeMoney from '../form/form-fields/TypeMoney';
import TypeText from '../form/form-fields/TypeText';
import { updateOrCreate } from '../special/updateOrCreate';
import { ProjectAddElementButton } from '../exo/project/ProjectAddElementButton';
import ExoPositions from '../exo/ExoPositions';
import { PositionImage } from '../exo/positions/collapse-modules/PositionImage';
import { PositionDescription } from '../exo/positions/collapse-modules/PositionDescription';
import { PositionNumber } from '../exo/positions/collapse-modules/PositionNumber';
import { PositionText } from '../exo/positions/collapse-modules/PositionText';
import { PositionCopy } from '../exo/positions/collapse-modules/PositionCopy';
import PreviewPositions from '../preview/PreviewPositions';
import DeleteIcon from '@mui/icons-material/Delete';
import { AddInvoice } from './order-invoices/AddInvoice';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import RemoveIcon from '@mui/icons-material/Remove';
import MoneyLabel from '../label/MoneyLabel';
import TypeDate from '../form/form-fields/TypeDate';
import TypeNumber from '../form/form-fields/TypeNumber';
import { numberToString } from '../special/numberConverter';
import DateLabel from '../label/DateLabel';
import { shouldGenerateFinalPositions } from '../new/NewProgressBilling';
import { round } from '../special/round';
import { getInvoiceDeduction } from '../special/orderEffectiveAmount';
import {
  appendDeductionGroup,
  buildDeductionPositions,
  hasDeductionLine,
} from '../special/deductionPositions';
import { buildOrderData, excludeInvoiceFromOrder } from '../special/buildOrderData';

// Removes the generated final-invoice summary block so it can be rebuilt.
// Generated lines are identified by their position type: everything the
// generator emits is type 'final' (PositionTypeEnum::FINAL on the backend),
// which — unlike the client-only isGenerated flag — survives a save/reload
// round trip. Children of a generated group (the HOAI phase copies keep their
// own type) are removed together with their group.
export function stripGeneratedPositions(positions) {
  if (!positions) return [];
  const kept = [];
  let generatedGroupDepth = null;
  positions.forEach((pos) => {
    const depth = pos.depth || 0;
    if (generatedGroupDepth != null) {
      if (depth > generatedGroupDepth) return;
      generatedGroupDepth = null;
    }
    if (pos.isGenerated || pos.type === 'final') {
      if (pos.isGroup) generatedGroupDepth = depth;
      return;
    }
    kept.push(pos);
  });
  return kept;
}

const StepOrderInvoives = ({
  totalAmount,
  apiUrl,
  apiFilter = 'status[ne][]=1&status[ne][]=2',
  dataCollection,
  updateDataCollection,
  activeStep,
  setActiveStep,
  step,
  updateValidation,
  button,
  onClose = () => {},
}) => {
  const { t } = useTranslation();

  // True only when the summary positions should be auto-generated: the
  // template carries a final-invoice typeCode AND doesn't opt out via
  // metaData.generateFinalPositions: false. Opted-out final invoices behave
  // like any other partial invoice.
  const generatesFinalPositions = shouldGenerateFinalPositions(
    dataCollection?.templateMetaData,
  );

  const { id, recipientType, recipient } = dataCollection.order || {};
  const { apiData } = useFetch(`orders/${id}`);

  const [openAddInvoice, setOpenAddInvoice] = useState(false);

  const { volume } = apiData?.data || {};

  useEffect(() => {
    if (!(apiData && apiData.data.invoices)) return;

    const orderChanged = dataCollection.orderId !== id;
    // Generating the summary positions needs the template meta (HOAI /
    // order-ineffective flags); if it isn't loaded yet the effect re-runs
    // once it arrives.
    const canGenerate =
      generatesFinalPositions && Boolean(dataCollection.templateMetaData);

    if (orderChanged) {
      // First load / order switch: drop the edited invoice's own contribution
      // when it's already counted (non-draft), then map to the flattened
      // order fields. orderData includes previousInvoicesSum. Same logic
      // seeds the initial edit state.
      const order = excludeInvoiceFromOrder(apiData.data, dataCollection);
      const orderData = buildOrderData(order, dataCollection);

      // Final invoices (unless the template opts out) always (re)generate
    // their order-summary positions.
      // When editing an existing invoice, keep its saved positions — otherwise
      // loading the order here would wipe them and the "Custom Positions" step
      // would show an empty table. New invoices start with no positions (they
      // are picked in the later position steps).
      let newPositions = dataCollection.id
        ? dataCollection.positions || []
        : [];
      if (canGenerate) {
        newPositions = generateInvoicePositions({
          ...dataCollection,
          ...orderData,
        });
      }

      updateDataCollection({
        ...orderData,
        positions: newPositions,
        orderId: id,
      });
      return;
    }

    // Editing a final invoice (orderId already matches): the order may have
    // been adjusted since the invoice was written, so the saved summary
    // positions can carry stale amounts (e.g. the old order total). Refresh
    // the flattened order fields from this step's own fetch and regenerate
    // the summary positions. The previous-invoice selection (and its sum) is
    // deliberately left untouched so invoices the user removed in this
    // session don't reappear on a revisit of the step.
    if (canGenerate) {
      const order = excludeInvoiceFromOrder(apiData.data, dataCollection);
      const { previousInvoices, previousInvoicesSum, ...orderData } =
        buildOrderData(order, dataCollection);
      updateDataCollection({
        ...orderData,
        positions: generateInvoicePositions({
          ...dataCollection,
          ...orderData,
        }),
      });
    }
  }, [apiData, dataCollection.templateMetaData]);

  function generateInvoicePositions(data) {
    const {
      orderDiscountPercent,
      orderPrice,
      orderTaxAmount,
      previousInvoices,
      positions,
      orderPositions,
      order,
      orderTaxRate,
      templateMetaData,
    } = data;

    var openOrderSum = 0;
    orderPositions.forEach((element) => {
      openOrderSum += round(
        round(element.price * element.amount) - parseFloat(element.billedTotal),
      );
    });

    // calc already invoiced amounts
    var invoicedAmount = 0;

    previousInvoices?.forEach((inv) => {
      invoicedAmount += round(inv.orderEffectiveAmountNetto);
    });

    // genearte the final positions
    let newPositions = stripGeneratedPositions(positions);

    newPositions.push({
      position: 1,
      name: `${order?.nr} ${t('Order Total Amount')}`,
      description: '',
      amount: 1,
      price: 0,
      taxRate: 0,
      status: 0,
      position: 1,
      type: 'final',
      unit: '',
      depth: 0,
      isPostPosition: false,
      positionableType: 'custom',
      isOrderPosition: true,
      isGroup: true,
      isGenerated: true,
    });

    if (templateMetaData.isHoaiTemplate) {
      orderPositions.forEach((element) => {
        newPositions.push({
          ...element,
          position: 1,
          taxRate: element.taxRate ?? orderTaxRate ?? 19,
          status: 0,
          depth: parseInt(element.depth) + 1,
          isPostPosition: false,
          isOrderPosition: true,
          isGenerated: true,
        });
      });
    } else {
      newPositions.push({
        position: 1,
        name: t('Order Total Amount'),
        description: '',
        amount: 1,
        price: orderPrice,
        taxRate: orderTaxRate ?? 19,
        type: 'final',
        status: 0,
        position: 1,
        unit: '',
        depth: 1,
        isPostPosition: false,
        positionableType: 'custom',
        isOrderPosition: true,
        isGenerated: true,
      });
    }

    if (orderDiscountPercent && orderDiscountPercent > 0)
      newPositions.push({
        position: 1,
        name: `${orderDiscountPercent}% ${t('rebate')}`,
        description: '',
        price: orderPrice,
        amount: -round(parseFloat(orderDiscountPercent) / 100),
        taxRate: orderTaxRate ?? 19,
        // Must stay 'percent': the XRechnung/Zugferd mappers convert
        // negative percent positions into BR-27-compliant line allowances.
        // Deduplication on reload is handled structurally — the line sits
        // inside the generated (type 'final') group, so
        // stripGeneratedPositions removes it with the group's subtree.
        type: 'percent',
        status: 0,
        position: 1,
        unit: '',
        depth: 1,
        isPostPosition: false,
        positionableType: 'custom',
        isOrderPosition: false,
        isGenerated: true,
      });

    newPositions.push({
      position: 2,
      name: t('Previous invoices'),
      description: '',
      amount: 1,
      price: 0,
      taxRate: 0,
      type: 'final',
      status: 0,
      position: 1,
      unit: '',
      depth: 0,
      isPostPosition: false,
      positionableType: 'custom',
      isOrderPosition: true,
      isGroup: true,
      isGenerated: true,
    });
    previousInvoices?.forEach((inv, index) => {
      // Order-effective share plus — when the template renders it — the
      // "not related" remainder. An invoice with no order-linked share whose
      // amount is fully shown by the "not related" line doesn't get an
      // additional 0 € line.
      const deduction = getInvoiceDeduction(
        inv,
        templateMetaData.useOrderIneffective,
      );
      const showEffectiveLine = !(
        deduction.countIneffective && deduction.effective.netto === 0
      );

      // order effective
      if (showEffectiveLine)
        newPositions.push({
          position: index + 1,
          name: `${inv?.nr} ${inv?.title}`,
          description: '',
          amount: 1,
          price: -deduction.effective.netto,
          taxRate: inv.orderTaxRate ?? orderTaxRate ?? 19,
          type: 'final',
          status: 0,
          position: 1,
          unit: '',
          depth: 1,
          isPostPosition: false,
          positionableType: 'custom',
          isOrderPosition: true,
          isGroup: false,
          isGenerated: true,
        });
      if (deduction.countIneffective) {
        // order ineffective — not billed against the order, so the line must
        // not count as an order position
        newPositions.push({
          position: index + 1,
          name: `${t('not related')} - ${inv?.nr} ${inv?.title}`,
          description: '',
          amount: 1,
          price: -deduction.ineffective.netto,
          taxRate: inv.orderTaxRate ?? orderTaxRate ?? 19,
          type: 'final',
          status: 0,
          position: 1,
          unit: '',
          depth: 1,
          isPostPosition: false,
          positionableType: 'custom',
          isOrderPosition: false,
          isGroup: false,
          isGenerated: true,
        });
      }
    });
    return newPositions;
  }

  const {
    previousInvoices,
    orderTitle,
    orderPrice,
    orderNr,
    orderStartDate,
    orderTaxAmount,
  } = dataCollection;

  useEffect(() => {
    // validation check
    //console.log(orderTitle, orderPrice, orderTaxAmount, orderStartDate);

    if (
      orderTitle &&
      orderPrice &&
      orderNr &&
      orderTaxAmount !== undefined &&
      orderStartDate
    ) {
      updateValidation(true);
    } else {
      updateValidation(false);
    }
  }, [dataCollection]);

  function handleInvoiceChange(newInvoice) {
    const newInvoices = updateOrCreate(previousInvoices || [], newInvoice);
    handleDataCollectionChange({
      previousInvoices: newInvoices,
      previousInvoicesSum: calcSumOfInvoices(newInvoices),
    });
  }

  function handleRemoveInvoice(inv) {
    const newInvoices = previousInvoices.filter((obj) => obj.id !== inv.id);
    handleDataCollectionChange({
      previousInvoices: newInvoices,
      previousInvoicesSum: calcSumOfInvoices(newInvoices),
    });
  }

  function handleDataCollectionChange(newData) {
    // Keep the existing positions — also on a new invoice. Positions added
    // before this step is touched again (e.g. the "Deduct invoices" lines, or
    // order positions picked before coming back here) must survive a discount
    // change or an invoice being added/removed. Only final invoices regenerate
    // their order-summary positions. See the note in the apiData effect above.
    let newPositions = dataCollection.positions || [];
    if (generatesFinalPositions && apiData) {
      newPositions = generateInvoicePositions({
        ...dataCollection,
        ...newData,
      });
    }
    updateDataCollection({
      ...newData,
      positions: newPositions,
    });
  }

  function calcSumOfInvoices(invoices) {
    const useIneffective = dataCollection?.templateMetaData?.useOrderIneffective;
    var newSum = 0;
    invoices.forEach((inv) => {
      newSum += getInvoiceDeduction(inv, useIneffective).netto;
    });
    return newSum;
  }

  // "Deduct invoices" quick action, see special/deductionPositions.js.
  // Invoices that don't have a deduction line on this invoice yet.
  const deductableInvoices = (previousInvoices || []).filter(
    (inv) => !hasDeductionLine(dataCollection.positions, inv),
  );

  function handleDeductInvoices() {
    const fallbackTaxRate = dataCollection.orderTaxRate ?? 19;
    const deductions = deductableInvoices.flatMap((inv) =>
      buildDeductionPositions(inv, fallbackTaxRate),
    );
    if (deductions.length === 0) return;
    updateDataCollection({
      positions: appendDeductionGroup(
        dataCollection.positions,
        deductions,
        t('Previous invoices'),
      ),
    });
  }

  // Final invoices already generate one deduction line per previous invoice;
  // offering the quick action there would deduct them twice.
  const showDeductButton =
    !generatesFinalPositions && Boolean(previousInvoices?.length);

  return (
    <Box className="w-full h-full flex flex-col gap-2">
      <Typography fontWeight={600}>{t('Order')}</Typography>
      <Order
        dataCollection={dataCollection}
        updateDataCollection={handleDataCollectionChange}
      />
      <Box className="flex justify-between items-center gap-2">
        <Typography fontWeight={600}>{t('Invoices')}</Typography>
        {showDeductButton && (
          <Tooltip
            title={t(
              'Adds the selected invoices as negative positions in a group at the end of this invoice',
            )}
          >
            <span>
              <Button
                onClick={handleDeductInvoices}
                disabled={deductableInvoices.length === 0}
                variant="contained"
                disableElevation
                color="secondary"
                size="small"
              >
                <RemoveIcon className="mr-2" sx={{ fontSize: '16px' }} />
                {t('Deduct invoices')}
              </Button>
            </span>
          </Tooltip>
        )}
      </Box>
      {previousInvoices?.map((inv) => (
        <Invoice
          key={inv.id}
          invoice={inv}
          onChange={handleInvoiceChange}
          onRemove={handleRemoveInvoice}
        />
      ))}
      <ProjectAddElementButton
        addLabel={t('Add Invoice')}
        size="small"
        onClick={() => setOpenAddInvoice(true)}
      />
      <AddInvoice
        open={openAddInvoice}
        onClose={() => setOpenAddInvoice(false)}
        recipientType={recipientType}
        recipientId={recipient?.id}
        onSelect={handleInvoiceChange}
        existingInvoices={previousInvoices}
      />
    </Box>
  );
};

const Order = ({ dataCollection, updateDataCollection }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

  const {
    orderTitle,
    orderPositions,
    order,
    showOrderPositions,
    orderNr,
    orderPrice,
    orderStartDate,
    orderDiscountPercent,
    orderDiscount,
    orderTaxRate,
  } = dataCollection;

  const { id, nr } = order || {};

  function handleFormFieldChange(name, value) {
    updateDataCollection({ [name]: value });
  }

  function handleOrderPositionsChange(name, value) {
    var newOrderPrice = 0;
    var newOrderTaxAmount;
    value.forEach((element) => {
      if (element.isOptional) return;
      const price = parseFloat(element.price) * parseFloat(element.amount);
      newOrderPrice += price;
      newOrderTaxAmount += price * (parseFloat(element.taxRate) / 100);
    });

    updateDataCollection({
      [name]: value,
      orderPrice: round(newOrderPrice),
      orderTaxAmount: newOrderTaxAmount,
    });
  }

  function handleDiscountFormFieldChange(name, value) {
    // calc new discounts
    const newOrderDiscount = round((value / 100) * orderPrice);
    updateDataCollection({ [name]: value, orderDiscount: newOrderDiscount });
  }

  function handlePriceOrTaxChangeFieldChange(name, value) {
    var newData = {};

    const floatValue = parseFloat(value);

    if (name[0] == 'orderTaxRate') {
      newData = {
        orderTaxRate: round(floatValue),
        orderTaxAmount: (orderPrice || 0) * (floatValue / 100),
      };
    }
    if (name[0] == 'orderPrice') {
      newData = {
        orderPrice: round(floatValue),
        // No tax rate entered yet must not turn the tax amount into NaN
        orderTaxAmount: floatValue * ((orderTaxRate ?? 0) / 100),
      };
    }
    updateDataCollection(newData);
  }

  useEffect(() => {
    calcSum();
  }, [orderPositions]);

  function calcSum() {
    if (orderPositions && orderPositions.length > 0) {
      var newSum = 0;
      orderPositions.forEach((element) => {
        if (element.isOptional) return;
        const price = round(
          parseFloat(element.price) * parseFloat(element.amount),
        );
        newSum += price;
      });
      if (newSum !== orderPrice) updateDataCollection({ orderPrice: newSum });
    }
  }

  function handleRemove() {
    updateDataCollection({
      orderTitle: '',
      orderPositions: [],
      order: null,
      // Also clear orderId: the populate effect is guarded by
      // `dataCollection.orderId !== id`, so a stale orderId would keep the
      // effect from re-seeding previousInvoices when an order is re-selected —
      // the next save would then sync an empty list and detach every
      // preceding invoice.
      orderId: null,
      showOrderPositions: false,
      orderDiscountPercent: null,
      orderNr: '',
      orderPrice: null,
      previousInvoices: [],
      orderStartDate: '',
      orderDiscount: null,
      orderTaxRate: dataCollection?.templateMetaData?.taxRate ?? 19,
    });
  }

  return (
    <Box
      sx={{
        border: `1px solid ${colors.card}`,
        '& .delButton': {
          transition: '0.3s ease',
          bgcolor: colors.error + '30',
          color: colors.error,
          border: `1px solid ${colors.error}80`,
          opacity: 0,
          transform: 'translate(-5px)',
        },
        ':hover': {
          '& .editButton, & .delButton': {
            opacity: 1,
            transform: 'translate(0px)',
          },
        },
      }}
      className="px-3 py-2 rounded-lg flex flex-col gap-2"
    >
      <Box className="flex gap-4 justify-between">
        <Box className="flex flex-col">
          <Box className="flex gap-2 items-start">
            <Typography className="w-full pb-2" variant="h4">
              <span style={{ fontWeight: 600 }}>{orderNr}</span>
              {` -  ${orderTitle || ''}`}
            </Typography>
            <IconButton
              onClick={handleRemove}
              size="small"
              className="delButton"
            >
              <DeleteIcon sx={{ fontSize: '15px' }} />
            </IconButton>
          </Box>
          {orderStartDate && (
            <Typography>
              <DateLabel date={orderStartDate} />
            </Typography>
          )}
        </Box>
        {orderPrice && (
          <Box className="flex flex-col gap-2 w-[180px]">
            <Box>
              <Typography
                fontWeight={600}
                fontSize="16px"
                className="opacity-50"
                textAlign="right"
              >
                <MoneyLabel money={orderPrice} />
              </Typography>
              {Boolean(orderDiscount) && (
                <Typography
                  fontWeight={500}
                  fontSize="14px"
                  className="opacity-50"
                  textAlign="right"
                  color="warning"
                >
                  {`-${numberToString(orderDiscountPercent, 1)}% `}
                  <span className=" italic">
                    (<MoneyLabel money={orderPrice - orderDiscount} />)
                  </span>
                </Typography>
              )}
            </Box>
            <TypeNumber
              field={{
                label: t('Order Discount'),
                key: 'orderDiscountPercent',
                unit: '%',
                decimalPlaces: 1,
              }}
              size="small"
              dataCollection={dataCollection}
              updateValidationOnChange={handleDiscountFormFieldChange}
            />
          </Box>
        )}
      </Box>
      {!id && (
        <Box className="pt-1 grid md:grid-cols-2 lg:grid-cols-4 gap-y-4 gap-1">
          <TypeText
            field={{ label: t('Nr'), key: 'orderNr' }}
            size="small"
            dataCollection={dataCollection}
            updateValidationOnChange={handleFormFieldChange}
          />
          <Box className="md:col-span-2 lg:col-span-3">
            <TypeText
              field={{ label: t('Title'), key: 'orderTitle' }}
              size="small"
              dataCollection={dataCollection}
              updateValidationOnChange={handleFormFieldChange}
            />
          </Box>

          {!showOrderPositions && (
            <>
              <TypeNumber
                field={{ label: t('Tax Rate'), key: 'orderTaxRate', unit: '%' }}
                size="small"
                dataCollection={dataCollection}
                updateValidationOnChange={handlePriceOrTaxChangeFieldChange}
              />

              <TypeMoney
                field={{ label: t('Total Amount'), key: 'orderPrice' }}
                size="small"
                dataCollection={dataCollection}
                updateValidationOnChange={handlePriceOrTaxChangeFieldChange}
              />
            </>
          )}
          <Box>
            <TypeDate
              field={{ label: t('Date'), key: 'orderStartDate' }}
              size="small"
              dataCollection={dataCollection}
              updateValidationOnChange={handleFormFieldChange}
            />
          </Box>
        </Box>
      )}
      {!showOrderPositions ? (
        <Box>
          <Button
            onClick={() => handleFormFieldChange('showOrderPositions', true)}
            variant="contained"
            disableElevation
            color="secondary"
            size="small"
          >
            <RemoveRedEyeIcon className="mr-2" sx={{ fontSize: '16px' }} />
            {t('Show all positions in invoice')}
          </Button>
        </Box>
      ) : (
        <PositionsRendering
          allowEdit={!Boolean(id)}
          positions={orderPositions || []}
          onRemove={() => handleFormFieldChange('showOrderPositions', false)}
          onChange={(name, newPositions) =>
            handleOrderPositionsChange('orderPositions', newPositions)
          }
        />
      )}
    </Box>
  );
};

const Invoice = ({ invoice, onChange, onRemove }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

  const { title, nr, showPositions, positions, amount } = invoice;

  function handleChange(key, value) {
    onChange({ ...invoice, [key]: value });
  }

  return (
    <Box
      sx={{
        border: `1px solid ${colors.card}`,
        '& .delButton': {
          transition: '0.3s ease',
          bgcolor: colors.error + '30',
          color: colors.error,
          border: `1px solid ${colors.error}80`,
          opacity: 0,
          transform: 'translate(-5px)',
        },
        ':hover': {
          '& .editButton, & .delButton': {
            opacity: 1,
            transform: 'translate(0px)',
          },
        },
      }}
      className="px-3 py-2 rounded-lg"
    >
      <Box className="flex w-full justify-between">
        <Box className="flex gap-2 items-start">
          <Typography className="w-full pb-2" variant="h4">
            <span style={{ fontWeight: 600 }}>{nr}</span>
            {` -  ${title}`}
          </Typography>
          <IconButton
            onClick={() => onRemove(invoice)}
            size="small"
            className="delButton"
          >
            <DeleteIcon sx={{ fontSize: '15px' }} />
          </IconButton>
        </Box>

        <Typography fontWeight={600} fontSize="16px" className="opacity-50">
          <MoneyLabel money={amount} />
        </Typography>
      </Box>
      {positions ? (
        !showPositions ? (
          <Button
            onClick={() => handleChange('showPositions', true)}
            variant="contained"
            disableElevation
            color="secondary"
            size="small"
          >
            <RemoveRedEyeIcon className="mr-2" sx={{ fontSize: '16px' }} />
            {t('Show all positions in invoice')}
          </Button>
        ) : (
          <PositionsRendering
            positions={positions}
            onChange={handleChange}
            onRemove={() => handleChange('showPositions', false)}
          />
        )
      ) : (
        <></>
      )}
    </Box>
  );
};

const PositionsRendering = ({ allowEdit, onChange, positions, onRemove }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

  return (
    <Box
      sx={{
        bgcolor: allowEdit ? 'transparent' : colors.card,
        '& .delButton': {
          transition: '0.3s ease',
          bgcolor: colors.error + '30',
          color: colors.error,
          border: `1px solid ${colors.error}80`,
          opacity: 0,
          transform: 'translate(-5px)',
        },
        ':hover': {
          '& .editButton, & .delButton': {
            opacity: 1,
            transform: 'translate(0px)',
          },
        },
      }}
      className="rounded-lg px-0 py-1 relative overflow-hidden"
    >
      <IconButton
        onClick={onRemove}
        size="small"
        className="delButton  z-10 backdrop-blur-sm"
        sx={{
          position: 'absolute',
          left: '5px',
          top: allowEdit ? '7px' : 'unset',
          bottom: allowEdit ? 'unset' : '5px',
        }}
      >
        <DeleteIcon sx={{ fontSize: '15px' }} />
      </IconButton>

      {allowEdit ? (
        <ExoPositions
          onPositionChange={(newPositions) =>
            onChange('positions', newPositions)
          }
          collapseModules={[
            { component: PositionImage, key: 'image', label: 'Image' },
            {
              component: PositionDescription,
              key: 'description',
              label: 'Description',
            },
            {
              component: PositionNumber,
              key: 'taxRate',
              label: 'Tax Rate',
              unit: '%',
              decimalPlaces: 0,
            },
            {
              component: PositionText,
              key: 'unit',
              label: 'Unit',
            },
            {
              component: PositionCopy,
              label: 'Duplicate position',
            },
          ]}
        />
      ) : (
        <PreviewPositions positions={positions} />
      )}
    </Box>
  );
};

export default StepOrderInvoives;
